/**
 * Quantos ciclos o link/recorrência precisa ter para cobrir a composição (SC-10950).
 *
 * O campo "Ciclos" do formulário era derivado só de `product.total_cycles`, o teto do PLANO.
 * Mexer na janela de ciclos do item nunca movia esse campo, então link e item divergiam: um
 * plano de 31 ciclos publicado num link cujo item vai até 12 gravava `total_cycles = 31` no
 * link. O BASE gera as cobranças pela janela do ITEM, e o link ficava prometendo 19 ciclos
 * que nunca existiriam.
 *
 * A janela do item manda quando está configurada; na falta dela vale o teto legado do plano.
 * É a mesma precedência de `BillingItem#applies_to_cycle?` e de
 * `ProductLink#billing_items_cycle_ceiling` no BASE — os dois lados precisam concordar.
 */
export type CycleWindowItem = {
  /** Janela do item: quantidade de COBRANÇAS a partir de startCycle. 0 = todo ciclo. */
  totalCycles?: number | null
  startCycle?: number | null
  /** De quantos em quantos ciclos o item é cobrado, ancorado no startCycle. */
  cycleInterval?: number | null
  /** Teto legado do plano, usado quando o item não tem janela própria. 0 = infinito. */
  productTotalCycles?: number | null
}

/**
 * Último ciclo em que o item ainda é cobrado, ou 0 quando ele é ilimitado.
 *
 * `totalCycles` conta COBRANÇAS, não ciclos: com passo, a última cai em
 * `start + (total - 1) * interval`. Com interval 1 os dois coincidem, que é o caso comum.
 */
const itemCeiling = (item: CycleWindowItem): number => {
  const own = Number(item.totalCycles ?? 0)
  const fromProduct = Number(item.productTotalCycles ?? 0)

  // Item sem janela própria herda o teto do plano — e um plano infinito (0) mantém o item
  // infinito, que é o que o BASE faz em apply_product_cycle_window_default!.
  const total = own > 0 ? own : fromProduct
  if (total <= 0) return 0

  const start = Math.max(Number(item.startCycle ?? 1), 1)
  const interval = Math.max(Number(item.cycleInterval ?? 1), 1)

  return start + (total - 1) * interval
}

/**
 * Teto da composição inteira, ou 0 ("indeterminado") quando qualquer item é ilimitado —
 * nesse caso não há teto derivável e quem decide segue sendo a escolha do merchant.
 */
export const deriveLinkTotalCycles = (items: CycleWindowItem[]): number => {
  if (!items || items.length === 0) return 0

  let max = 0

  for (const item of items) {
    const ceiling = itemCeiling(item)
    // Um único item ilimitado torna a composição inteira indeterminada: ela continua
    // cobrando depois que todos os outros acabam.
    if (ceiling === 0) return 0
    if (ceiling > max) max = ceiling
  }

  return max
}
